/**
 * Lightweight fetch wrapper for /api/*. Same-origin so the Access
 * cookie travels automatically. Throws a typed ApiError on non-2xx.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly payload: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
  isUnauthorized(): boolean {
    return this.status === 401;
  }
}

export interface Me {
  sub: string;
  email: string;
  name: string;
  role: 'admin' | 'viewer';
  env: string;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers ?? {}),
    },
  });
  const text = await res.text();
  const body = text ? (JSON.parse(text) as unknown) : undefined;
  if (!res.ok) {
    const msg =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: unknown }).error)
        : `HTTP ${res.status}`;
    throw new ApiError(msg, res.status, body);
  }
  return body as T;
}

export interface CostsSummary {
  total_gbp: number;
  per_service: { service: string; gbp: number }[];
  per_day: { day: string; gbp: number }[];
  window: { from: number; to: number };
}

export interface CostsByAutomation {
  source: string;
  total_gbp: number;
  items: { automation_id: string | null; automation_name: string | null; gbp: number; events: number }[];
}

export interface AutomationItem {
  id: string;
  event_id: string;
  occurred_at: number;
  source: string;
  event_type: string;
  automation_id: string | null;
  automation_name: string | null;
  status: string;
  severity: string | null;
  duration_ms: number | null;
  cost_gbp: number | null;
  error_code: string | null;
  error_message: string | null;
}

export interface SourceHealth {
  source: string;
  status: 'ok' | 'warning' | 'critical' | 'no_data';
  last_event_at: number | null;
  expected_window_sec: number;
  silent_for_sec: number | null;
}

export interface WebApp {
  name: string;
  title: string;
  category: string;
  description: string;
  url: string | null;
  urlStaging: string | null;
  repoUrl: string | null;
  version: string | null;
  versionSource: 'changelog' | 'package.json' | null;
  git: {
    present: boolean;
    lastPushed?: string | null;
    defaultBranch?: string | null;
    language?: string | null;
    lastCommit?: string | null;
    lastAuthor?: string | null;
  };
  cloudflare: { present: boolean; lastModified: string | null; resourceCount: number };
  docs: { readme: boolean; claude: boolean; changelog: boolean };
}

export interface WebAppsManifest {
  generatedAt: string;
  count: number;
  stats: { total: number; apps: number; live: number; deployedThisWeek: number; stale90: number };
  apps: WebApp[];
}

export const api = {
  me: () => request<Me>('/api/me'),
  webApps: (opts: { refresh?: boolean } = {}) =>
    request<WebAppsManifest>(`/api/web-apps${opts.refresh ? '?refresh=1' : ''}`),
  costsSummary: (params: { from: number; to: number }) =>
    request<CostsSummary>(`/api/costs/summary?from=${params.from}&to=${params.to}`),
  costsByAutomation: (params: { source: string; from: number; to: number }) =>
    request<CostsByAutomation>(
      `/api/costs/by-automation?source=${encodeURIComponent(params.source)}&from=${params.from}&to=${params.to}`,
    ),
  automations: (params: { from: number; to: number; source?: string; status?: string; cursor?: string }) => {
    const qs = new URLSearchParams({ from: String(params.from), to: String(params.to) });
    if (params.source) qs.set('source', params.source);
    if (params.status) qs.set('status', params.status);
    if (params.cursor) qs.set('cursor', params.cursor);
    return request<{ items: AutomationItem[]; next_cursor?: string }>(`/api/automations?${qs.toString()}`);
  },
  integrationsHealth: () => request<{ items: SourceHealth[] }>('/api/integrations/health'),
  alertRules: () => request<{ items: unknown[] }>('/api/alerts/rules'),
  alertHistory: (params: { from: number; to: number }) =>
    request<{ items: unknown[] }>(`/api/alerts/history?from=${params.from}&to=${params.to}`),
};
